/**
 * Verification message utilities
 *
 * Converts technical Matrix SDK error codes into user-friendly messages
 */

/**
 * Get user-friendly error message for verification failure/cancellation
 * @param {string} reason - Matrix SDK cancellation/error reason code or plain error message
 * @param {Function} t - Translation function from i18n context
 * @returns {string} User-friendly error message
 */
export const getVerificationErrorMessage = (reason, t) => {
  if (!reason) {
    return t('VerificationErrors.default', {}, 'Verification could not be completed. Please try again.');
  }

  // Convert to string if it's an object
  const reasonString = typeof reason === 'string' ? reason : String(reason);

  const errorKeyMap = {
    // User actions
    'user': 'userCancelled',
    'm.user': 'userCancelled',

    // Mismatched emojis - most common user error
    'mismatch': 'mismatchedSas',
    'm.mismatched_sas': 'mismatchedSas',
    'm.mismatched_commitment': 'mismatchedSas',

    // Timeouts
    'timeout': 'timeout',
    'm.timeout': 'timeout',

    // Technical/protocol issues
    'm.unexpected_message': 'unexpectedMessage',
    'm.unknown_transaction': 'unknownTransaction',
    'm.unknown_method': 'unknownMethod',
    'm.invalid_message': 'invalidMessage',
    'm.accepted': 'acceptedElsewhere',

    // Key mismatches - security concern
    'm.key_mismatch': 'keyMismatch',

    // QR code specific
    'm.qr_code.scan_error': 'qrScanError',
    'm.qr_code.invalid': 'qrInvalid',
  };

  // Fallback messages for when translations are missing
  const fallbackMessages = {
    'user': 'Verification was cancelled',
    'm.user': 'Verification was cancelled',
    'mismatch': 'The verification codes didn\'t match. Please try again.',
    'm.mismatched_sas': 'The verification codes didn\'t match. Please try again.',
    'm.mismatched_commitment': 'The verification codes didn\'t match. Please try again.',
    'timeout': 'Verification timed out. Please try again.',
    'm.timeout': 'Verification timed out. Please try again.',
    'm.unexpected_message': 'Something went wrong. Please try verification again.',
    'm.unknown_transaction': 'Connection lost. Please start a new verification.',
    'm.unknown_method': 'This verification method is not supported.',
    'm.invalid_message': 'Invalid verification message received. Please try again.',
    'm.accepted': 'Verification request was accepted elsewhere.',
    'm.key_mismatch': 'Security keys don\'t match. This might be a different device.',
    'm.qr_code.scan_error': 'QR code could not be scanned. Please try again.',
    'm.qr_code.invalid': 'Invalid QR code. Please try again.',
  };

  // Check for exact match
  if (errorKeyMap[reasonString]) {
    const translationKey = `VerificationErrors.${errorKeyMap[reasonString]}`;
    const fallback = fallbackMessages[reasonString] || 'Verification could not be completed. Please try again.';
    return t(translationKey, {}, fallback);
  }

  // Check for partial matches for Matrix error codes (in case of complex reason strings)
  if (reasonString.includes('mismatch') || reasonString.includes('sas')) {
    return t('VerificationErrors.mismatchedSas', {}, 'The verification codes didn\'t match. Please try again.');
  }
  if (reasonString.includes('timeout')) {
    return t('VerificationErrors.timeout', {}, 'Verification timed out. Please try again.');
  }
  if (reasonString.includes('cancelled') || reasonString.includes('cancel')) {
    return t('VerificationErrors.userCancelled', {}, 'Verification was cancelled');
  }

  // Check if this looks like a Matrix error code (starts with "m." or is a known keyword)
  const isMatrixErrorCode = reasonString.startsWith('m.') ||
    ['user', 'mismatch', 'timeout'].includes(reasonString);

  // If it's not a Matrix error code, it's likely a plain error message - return it as-is
  if (!isMatrixErrorCode) {
    return reasonString;
  }

  // Default friendly message for unrecognized Matrix error codes
  return t('VerificationErrors.default', {}, 'Verification could not be completed. Please try again.');
};

